import * as React from "react";
import { Input as ShadcnInput } from "../input";
import { Textarea as ShadcnTextarea } from "../textarea";
import { cn } from "@/lib/utils";

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  size?: "sm" | "md" | "lg";
  radius?: "none" | "sm" | "md" | "lg" | "xl" | "full";
  variant?: "flat" | "bordered" | "faded" | "underlined";
  label?: React.ReactNode;
  labelPlacement?: "inside" | "outside";
  placeholder?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  description?: string;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  classNames?: {
    base?: string;
    input?: string;
    label?: string;
    description?: string;
    errorMessage?: string;
  };
  onValueChange?: (value: string) => void;
  value?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = "md",
      radius = "xl",
      variant = "bordered",
      label,
      labelPlacement = "inside",
      placeholder,
      isDisabled,
      isReadOnly,
      disabled,
      readOnly,
      isRequired,
      isInvalid,
      errorMessage,
      description,
      startContent,
      endContent,
      classNames,
      onValueChange,
      onChange,
      value,
      className,
      ...props
    },
    ref
  ) => {
    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onValueChange) {
          onValueChange(e.target.value);
        }
        onChange?.(e);
      },
      [onValueChange, onChange]
    );

    const radiusClass = React.useMemo(() => {
      if (radius === "none") return "rounded-none";
      if (radius === "sm") return "rounded-sm";
      if (radius === "md") return "rounded-md";
      if (radius === "lg") return "rounded-lg";
      if (radius === "xl") return "rounded-xl";
      if (radius === "full") return "rounded-full";
      return "";
    }, [radius]);

    const sizeClass = React.useMemo(() => {
      if (size === "sm") return "h-9 text-sm";
      if (size === "lg") return "h-14 text-base";
      return "h-12";
    }, [size]);

    const hasStartContent = !!startContent;
    const hasEndContent = !!endContent;

    return (
      <div className={cn("relative w-full", classNames?.base)}>
        {label && labelPlacement === "outside" && (
          <label
            className={cn("block text-sm font-medium mb-1", classNames?.label)}
          >
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {hasStartContent && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
              {startContent}
            </div>
          )}
          <ShadcnInput
            ref={ref}
            value={value}
            placeholder={placeholder}
            disabled={disabled ?? isDisabled}
            readOnly={readOnly ?? isReadOnly}
            onChange={handleChange}
            className={cn(
              radiusClass,
              sizeClass,
              hasStartContent && "pl-10",
              hasEndContent && "pr-10",
              isInvalid && "border-red-500",
              classNames?.input,
              className
            )}
            {...props}
          />
          {hasEndContent && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
              {endContent}
            </div>
          )}
        </div>
        {description && !errorMessage && (
          <p
            className={cn(
              "text-muted-foreground mt-1",
              classNames?.description
            )}
          >
            {description}
          </p>
        )}
        {errorMessage && (
          <p
            className={cn(
              "text-sm text-red-500 mt-1",
              classNames?.errorMessage
            )}
          >
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export interface TextareaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "size"
> {
  size?: "sm" | "md" | "lg";
  radius?: "none" | "sm" | "md" | "lg" | "xl" | "full";
  variant?: "flat" | "bordered" | "faded" | "underlined";
  label?: React.ReactNode;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  description?: string;
  isClearable?: boolean;
  labelPlacement?: "inside" | "outside";
  minRows?: number;
  maxRows?: number;
  onClear?: () => void;
  classNames?: {
    base?: string;
    input?: string;
    label?: string;
    description?: string;
    errorMessage?: string;
    innerWrapper?: string;
  };
  onValueChange?: (value: string) => void;
  value?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      size = "md",
      radius = "xl",
      variant = "bordered",
      label,
      isDisabled,
      isReadOnly,
      disabled,
      readOnly,
      isRequired,
      isInvalid,
      errorMessage,
      description,
      isClearable,
      labelPlacement = "inside",
      minRows,
      maxRows,
      onClear,
      classNames,
      onValueChange,
      onChange,
      value,
      className,
      rows,
      ...props
    },
    ref
  ) => {
    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (onValueChange) {
          onValueChange(e.target.value);
        }
        onChange?.(e);
      },
      [onValueChange, onChange]
    );

    const radiusClass = React.useMemo(() => {
      if (radius === "none") return "rounded-none";
      if (radius === "sm") return "rounded-sm";
      if (radius === "md") return "rounded-md";
      if (radius === "lg") return "rounded-lg";
      if (radius === "xl") return "rounded-xl";
      if (radius === "full") return "rounded-full";
      return "";
    }, [radius]);

    const finalRows = rows ?? minRows ?? maxRows;

    return (
      <div className={cn("relative w-full", classNames?.base)}>
        {label && labelPlacement === "outside" && (
          <label
            className={cn("block text-sm font-medium mb-1", classNames?.label)}
          >
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className={cn("relative", classNames?.innerWrapper)}>
          <ShadcnTextarea
            ref={ref}
            value={value}
            disabled={disabled ?? isDisabled}
            readOnly={readOnly ?? isReadOnly}
            onChange={handleChange}
            rows={finalRows}
            className={cn(
              radiusClass,
              isInvalid && "border-red-500",
              classNames?.input,
              className
            )}
            {...props}
          />
          {isClearable && value && (
            <button
              type="button"
              onClick={onClear}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
        {label && labelPlacement === "inside" && (
          <label
            className={cn("block text-sm font-medium mb-1", classNames?.label)}
          >
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        {description && !errorMessage && (
          <p
            className={cn(
              "text-sm text-muted-foreground mt-1",
              classNames?.description
            )}
          >
            {description}
          </p>
        )}
        {errorMessage && (
          <p
            className={cn(
              "text-sm text-red-500 mt-1",
              classNames?.errorMessage
            )}
          >
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);
Textarea.displayName = "Textarea";

export { Input, Textarea };
